import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  Eye,
  X,
  MapPin,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  Download,
  Search,
  FileText,
  Activity,
  Navigation,
  FileBox,
} from "lucide-react";
import api, { DRIVER_IMAGE_URL } from "../api/Api.js";
import "./DriverLoginStatusPage.css";

// ############################################################
// 🔧 DATE UTILITIES

const reliableDateParse = (dateString) => {
  if (!dateString || dateString === "NULL") return null;
  if (
    typeof dateString === "string" &&
    dateString.includes("/") &&
    dateString.includes(",")
  ) {
    try {
      const [datePart, timePart] = dateString.split(",");
      const [day, month, year] = datePart.trim().split("/").map(Number);
      let [time, modifier] = timePart.trim().split(" ");
      let [hours, minutes, seconds] = time.split(":").map(Number);
      if (modifier) {
        modifier = modifier.toLowerCase();
        if (hours === 12) hours = 0;
        if (modifier === "pm") hours += 12;
      }
      return new Date(year, month - 1, day, hours, minutes, seconds || 0);
    } catch (e) {
      console.warn("Custom date parse failed:", e);
    }
  }
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = reliableDateParse(value);
  if (!d) return { date: "--", time: "--" };
  return {
    date: d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }),
  };
};

const formatTimeOnly = (value) => {
  const d = reliableDateParse(value);
  if (!d) return "--:--";
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const TableSkeleton = () => {
  return (
    <div className="skeleton-wrapper">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="skeleton-row">
          <div className="skeleton-cell w-40"></div>
          <div className="skeleton-cell w-150"></div>
          <div className="skeleton-cell w-80"></div>
          <div className="skeleton-cell w-100"></div>
          <div className="skeleton-cell w-flex"></div>
          <div className="skeleton-cell w-flex"></div>
        </div>
      ))}
    </div>
  );
};

const AttendanceTable = () => {
  const [loginData, setLoginData] = useState([]);
  const [attendanceList, setAttendanceList] = useState([]);
  const [punchFilter, setPunchFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const location = useLocation();

  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get("filter");
    if (filterParam === "DutyStart") {
      setPunchFilter("DutyStart");
    } else if (filterParam === "DutyEnd") {
      setPunchFilter("DutyEnd");
    }
  }, [location.search]);

  // 🔄 FETCH LOGIC
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [loginRes, attRes] = await Promise.all([
        api
          .get(`/drvlogsts/DriverLoginData/${fromDate}/${toDate}`)
          .catch((err) => {
            console.error("Login Fetch Error:", err);
            return { data: { data: [] } };
          }),
        api
          .get(`/drvlogsts/attendanceGetData/${fromDate}/${toDate}`)
          .catch((err) => {
            console.error("Attendance Fetch Error:", err);
            return { data: { data: [] } };
          }),
      ]);

      let allLoginData = loginRes.data?.data || [];
      let allAttendanceData = attRes.data?.data || [];
      // console.log("attendance data--", allAttendanceData)

      allLoginData.sort((a, b) => {
        const dateA = new Date(a.dateKey);
        const dateB = new Date(b.dateKey);
        const dateDiff = dateB - dateA;
        if (dateDiff === 0) {
          return a.driver_fstname.localeCompare(b.driver_fstname);
        }
        return dateDiff;
      });

      setLoginData(allLoginData);
      setAttendanceList(allAttendanceData);
    } catch (err) {
      console.error("Global Fetch Error:", err);
      toast.error("Error loading data range");
    } finally {
      setLoading(false);
    }
  };

  // 🔍 MATCHING & FILTERING
  const getAttendanceByDriverAndDate = (regNo, dateKey) => {
    return (
      attendanceList.find(
        (att) => att.driver_regno === regNo && att.dateKey === dateKey,
      ) || null
    );
  };

  const getFilteredData = () => {
    return loginData.filter((emp) => {
      const att = getAttendanceByDriverAndDate(emp.driver_regno, emp.dateKey);

      const hasLoggedIn = !!emp.login_datetime;
      const hasPunchedIn =
        att?.checkin_datetime && att.checkin_datetime !== "NULL";
      const hasPunchedOut =
        att?.checkout_datetime && att.checkout_datetime !== "NULL";
      const hasDutyStarted =
        att?.duty_in_datetime && att.duty_in_datetime !== "NULL";
      const hasDutyEnded =
        att?.duty_out_datetime && att.duty_out_datetime !== "NULL";

      const search = searchTerm.toLowerCase();
      const fullName =
        `${emp.driver_fstname} ${emp.driver_mdlname} ${emp.driver_lstname}`.toLowerCase();
      const matchesSearch =
        fullName.includes(search) ||
        emp.driver_regno.toLowerCase().includes(search);

      let matchesStatus = false;

      if (punchFilter === "All") matchesStatus = true;
      else if (punchFilter === "Punch") matchesStatus = hasPunchedIn;
      else if (punchFilter === "Not-Punch")
        matchesStatus = hasLoggedIn && !hasPunchedIn;
      else if (punchFilter === "PunchOut") matchesStatus = hasPunchedOut;
      else if (punchFilter === "Not-PunchOut")
        matchesStatus = hasPunchedIn && !hasPunchedOut;
      else if (punchFilter === "DutyStart") matchesStatus = hasDutyStarted;
      else if (punchFilter === "DutyEnd") matchesStatus = hasDutyEnded;
      else if (punchFilter === "Not-Login") matchesStatus = !hasLoggedIn;

      return matchesStatus && matchesSearch;
    });
  };

  const filteredData = getFilteredData();

  const handleViewDetails = (emp) => {
    setSelectedDriver({
      ...emp,
      attendance: getAttendanceByDriverAndDate(emp.driver_regno, emp.dateKey),
    });
  };

  // 📊 EXCEL EXPORT (UPDATED)
  const exportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error("No data to export");
      return;
    }

    const excelData = filteredData.map((emp, index) => {
      const attendance = getAttendanceByDriverAndDate(
        emp.driver_regno,
        emp.dateKey,
      );
      const loginDT = emp.login_datetime
        ? formatDateTime(emp.login_datetime)
        : { date: "Not Logged In", time: "" };
      const punchInDT = formatDateTime(attendance?.checkin_datetime);
      const punchOutDT = formatDateTime(attendance?.checkout_datetime);
      const dutyStartDT = formatDateTime(attendance?.duty_in_datetime);
      const dutyEndDT = formatDateTime(attendance?.duty_out_datetime);

      // Helper to format string with Newline for Excel
      const formatCell = (dtObj, loc) => {
        if (dtObj.time === "--") return "--";
        // \n creates a line break in Excel if wrapText is true
        return `${dtObj.date} ${dtObj.time}\n| ${loc || "Unknown Location"}`;
      };

      return {
        "Sl No": index + 1,
        Date: emp.dateKey,
        "Driver Info": `${emp.driver_fstname || ""} ${emp.driver_mdlname || ""} ${
          emp.driver_lstname || ""
        } (${emp.driver_regno}) ${emp.driver_phno || ""}`,
        Login: loginDT.time
          ? `${loginDT.date} ${loginDT.time}`
          : "Not Logged In",
        "Punch In": formatCell(punchInDT, attendance?.cigps_locname),
        "Duty Start": formatCell(dutyStartDT, attendance?.ci_location),
        "Punch Out": formatCell(punchOutDT, attendance?.cogps_locname),
        "Duty End": formatCell(dutyEndDT, attendance?.co_location),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set Column Widths (Wider for Location columns)
    worksheet["!cols"] = [
      { wch: 6 }, // Sl No
      { wch: 12 }, // Date
      { wch: 35 }, // Driver Info
      { wch: 20 }, // Login
      { wch: 45 }, // Punch In (Wide for location)
      { wch: 45 }, // Duty Start
      { wch: 45 }, // Punch Out
      { wch: 45 }, // Duty End
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Drivers_Activity");
    XLSX.writeFile(workbook, `Drivers_Activity_${fromDate}_to_${toDate}.xlsx`);
    toast.success("Excel downloaded successfully");
  };

  // 📝 WORD EXPORT (UPDATED)
  const exportToWord = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error("No data to export");
      return;
    }

    let tableHTML = `
      <table border="1" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; font-size: 10px;">
        <thead style="background-color: #f0f0f0;">
          <tr>
            <th style="padding: 5px; width: 30px;">#</th>
            <th style="padding: 5px; width: 70px;">Date</th>
            <th style="padding: 5px; width: 150px;">Driver Info</th>
            <th style="padding: 5px; width: 100px;">Login</th>
            <th style="padding: 5px;">Punch In / Loc</th>
            <th style="padding: 5px;">Duty Start / Loc</th>
            <th style="padding: 5px;">Punch Out / Loc</th>
            <th style="padding: 5px;">Duty End / Loc</th>
          </tr>
        </thead>
        <tbody>
    `;

    filteredData.forEach((emp, index) => {
      const att = getAttendanceByDriverAndDate(emp.driver_regno, emp.dateKey);
      const loginDT = emp.login_datetime
        ? formatDateTime(emp.login_datetime)
        : { date: "", time: "Not Logged" };
      const pIn = formatDateTime(att?.checkin_datetime);
      const dStart = formatDateTime(att?.duty_in_datetime);
      const pOut = formatDateTime(att?.checkout_datetime);
      const dEnd = formatDateTime(att?.duty_out_datetime);

      // Helper for Word Cell (Using <br/> for line break)
      const renderCell = (dt, loc) => {
        if (dt.time === "--") return "--";
        return `
          <div style="margin-bottom: 4px;"><b>${dt.date} ${dt.time}</b></div>
          <div style="color: #444; font-size: 9px; word-wrap: break-word;">${loc || "Unknown Location"}</div>
        `;
      };

      tableHTML += `
        <tr>
          <td style="padding: 5px; text-align: center;">${index + 1}</td>
          <td style="padding: 5px;">${emp.dateKey}</td>
          <td style="padding: 5px;">
            <b>${emp.driver_fstname} ${emp.driver_mdlname} ${emp.driver_lstname}</b><br/>
            (${emp.driver_regno})<br/>
            ${emp.driver_phno || ""}
          </td>
          <td style="padding: 5px;">${loginDT.date}<br/>${loginDT.time}</td>
          <td style="padding: 5px;">${renderCell(pIn, att?.cigps_locname)}</td>
          <td style="padding: 5px;">${renderCell(dStart, att?.ci_location)}</td>
          <td style="padding: 5px;">${renderCell(pOut, att?.cogps_locname)}</td>
          <td style="padding: 5px;">${renderCell(dEnd, att?.co_location)}</td>
        </tr>
      `;
    });

    tableHTML += `</tbody></table>`;

    const header = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>Export HTML to Word Document with JavaScript</title></head><body>`;
    const footer = "</body></html>";
    const sourceHTML = header + tableHTML + footer;

    const source =
      "data:application/vnd.ms-word;charset=utf-8," +
      encodeURIComponent(sourceHTML);
    const fileDownload = document.createElement("a");
    document.body.appendChild(fileDownload);
    fileDownload.href = source;
    fileDownload.download = `Drivers_Report_${fromDate}.doc`;
    fileDownload.click();
    document.body.removeChild(fileDownload);
    toast.success("Word document downloaded");
  };

  return (
    <div className="page-container">
      <div className="pageTitle">
        <h2 className="title">
          Drivers Activity –{" "}
          <span style={{ fontSize: "0.9em", color: "#0051ff" }}>
            {new Date(fromDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
            })}
            {" to "}
            {new Date(toDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
        </h2>
      </div>

      <div className="attendance-wrapper1">
        <div className="controls-bar">
          <div className="left-controls">
            {/* FROM DATE */}
            <div className="d-flex flex-column">
              <span className="lbl-input">From</span>
              <input
                type="date"
                className="date-picker"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            {/* TO DATE */}
            <div className="d-flex flex-column">
              <span className="lbl-input">To</span>
              <input
                type="date"
                className="date-picker"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <button
              className="btn-view-records"
              onClick={fetchAllData}
              disabled={loading}
            >
              {loading ? "..." : "View"}
            </button>

            {/* STATUS FILTER */}
            <div className="d-flex flex-column">
              <span className="lbl-input">Status</span>
              <select
                className="attendance-select"
                value={punchFilter}
                onChange={(e) => setPunchFilter(e.target.value)}
              >
                <option value="All">All Drivers</option>
                <option value="Punch">Punched In</option>
                <option value="Not-Punch">Not Punched In</option>
                <option value="DutyStart">On Duty</option>
                <option value="DutyEnd">Duty Ended</option>
                <option value="PunchOut">Punched Out</option>
                <option value="Not-PunchOut">Not Punched Out</option>
                <option value="Not-Login">Not Logged In</option>
              </select>
            </div>

            {/* SEARCH */}
            <div className="search-wrapper">
              <Search size={16} className="search-icon-inside" />
              <input
                type="text"
                className="search-input"
                placeholder="Search Name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="right-controls">
            <button
              className="btn-export btn-word"
              onClick={exportToWord}
              disabled={loading}
              title="Export to Word"
            >
              <FileBox size={16} />
              <span>Word</span>
            </button>
            <button
              className="btn-export btn-excel"
              onClick={exportToExcel}
              disabled={loading}
              title="Export to Excel"
            >
              <Download size={16} />
              <span>Excel</span>
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="attendance-scroll">
          {loading ? (
            <TableSkeleton />
          ) : (
            <table className="login-status">
              <thead>
                <tr>
                  <th className="col-sl">#</th>
                  <th className="col-action">Action</th>
                  <th className="col-driver">Driver Info</th>
                  <th className="col-time">Login Time</th>
                  <th className="col-loc">Punch In/LOC</th>
                  <th className="col-loc">Duty Start/LOC</th>
                  <th className="col-loc">Punch Out/LOC</th>
                  <th className="col-loc">Duty End/LOC</th>
                  <th className="col-loc text-center">Odo (St/End) & Tot</th>
                  </tr>
              </thead>
              <tbody>
                {filteredData.map((emp, i) => {
                  const att = getAttendanceByDriverAndDate(
                    emp.driver_regno,
                    emp.dateKey,
                  );
                  const login = emp.login_datetime
                    ? formatDateTime(emp.login_datetime)
                    : { date: "--", time: "--" };
                  const pIn = formatDateTime(att?.checkin_datetime);
                  const pOut = formatDateTime(att?.checkout_datetime);
                  const dStart = formatDateTime(att?.duty_in_datetime);
                  const dEnd = formatDateTime(att?.duty_out_datetime);

                  const rowKey = `${emp.driver_regno}_${emp.dateKey}_${i}`;

                  return (
                    <tr key={rowKey}>
                      <td className="text-center">{i + 1}</td>
                      <td className="text-center">
                        <button
                          className="btn-view"
                          onClick={() => handleViewDetails(emp)}
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                      <td>
                        <div className="driver-name-cell">
                          <span className="d-name">
                            {emp.driver_fstname} {emp.driver_mdlname || ""}{" "}
                            {emp.driver_lstname}
                          </span>
                          <span className="d-reg">{emp.driver_regno}</span>
                          <span className="d-ph">
                            {emp.driver_phno || "--"}
                          </span>
                        </div>
                      </td>

                      <td>
                        {login.time !== "--" ? (
                          <div className="dt-cell">
                            <span className="dt-time">{login.time}</span>
                            <span className="dt-date">{login.date}</span>
                          </div>
                        ) : (
                          <span className="no-data">--</span>
                        )}
                      </td>

                      <td
                        className="loc-cell"
                        data-tooltip={att?.cigps_locname || "No Location"}
                      >
                        {pIn.time !== "--" ? (
                          <div className="dt-cell">
                            <span className="dt-time success-text">
                              {pIn.time}
                            </span>
                            <span className="dt-date">{pIn.date}</span>
                          </div>
                        ) : (
                          <span className="no-data">--</span>
                        )}
                        <div className="text-truncate">
                          {att?.cigps_locname || ""}
                        </div>
                      </td>

                      <td
                        className="loc-cell"
                        data-tooltip={att?.ci_location || "No Location"}
                      >
                        {dStart.time !== "--" ? (
                          <div className="dt-cell">
                            <span className="dt-time warn-text">
                              {dStart.time}
                            </span>
                            <span className="dt-date">{dStart.date}</span>
                          </div>
                        ) : (
                          <span className="no-data">--</span>
                        )}
                        <div className="text-truncate">
                          {att?.ci_location || ""}
                        </div>
                      </td>

                      <td
                        className="loc-cell"
                        data-tooltip={att?.cogps_locname || "No Location"}
                      >
                        {pOut.time !== "--" ? (
                          <div className="dt-cell">
                            <span className="dt-time">{pOut.time}</span>
                            <span className="dt-date">{pOut.date}</span>
                          </div>
                        ) : (
                          <span className="no-data">--</span>
                        )}
                        <div className="text-truncate">
                          {att?.cogps_locname || ""}
                        </div>
                      </td>

                      <td
                        className="loc-cell"
                        data-tooltip={att?.co_location || "No Location"}
                      >
                        {dEnd.time !== "--" ? (
                          <div className="dt-cell">
                            <span className="dt-time danger-text">
                              {dEnd.time}
                            </span>
                            <span className="dt-date">{dEnd.date}</span>
                          </div>
                        ) : (
                          <span className="no-data">--</span>
                        )}
                        <div className="text-truncate">
                          {att?.co_location || ""}
                        </div>
                      </td>
                      
                      <td className="loc-cell text-center" style={{ verticalAlign: "middle" }}>
                          {att?.odo_civalue || att?.odo_covalue ? (
                            <div className="d-flex flex-column align-items-center justify-content-center w-100">
                             <span className="text-primary" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                St: {att.odo_civalue && att.odo_civalue !== "NULL" ? `${att.odo_civalue} KM` : "--"}
                              </span>
                              <span className="text-danger" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                End: {att.odo_covalue && att.odo_covalue !== "NULL" ? `${att.odo_covalue} KM` : "--"}
                              </span>
                             <span className="success-text fw-bold " style={{ fontSize: "12px", borderTop: "1px solid #eee", width: "80%", paddingTop: "2px" }}>
                                    Total: {
                                      att.odo_civalue && att.odo_covalue && 
                                      att.odo_civalue !== "NULL" && att.odo_covalue !== "NULL" &&
                                      !isNaN(att.odo_covalue) && !isNaN(att.odo_civalue)
                                        ? `${Number(att.odo_covalue) - Number(att.odo_civalue)} KM`
                                        : "--"
                                    }
                                  </span>
                            </div>
                          ) : (
                            <span className="no-data">--</span>
                          )}
                        </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 🔹 MODAL & LIGHTBOX */}
      {selectedDriver && (
        <DetailsModal
          driver={selectedDriver}
          attendance={selectedDriver.attendance}
          onClose={() => setSelectedDriver(null)}
          onImageClick={setPreviewImage}
        />
      )}
      {previewImage && (
        <div className="image-lightbox" onClick={() => setPreviewImage(null)}>
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={previewImage} alt="Full View" />
            <button
              className="close-lightbox"
              onClick={() => setPreviewImage(null)}
            >
              <X size={30} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ############################################################
//  COMPONENT: DETAILS MODAL 
const DetailsModal = ({ driver, attendance, onClose, onImageClick }) => {
  if (!attendance) return null;

  const formatList = (json) =>
    Array.isArray(json)
      ? json
      : typeof json === "object" && json !== null
        ? Object.values(json)
        : [];
  const startChecklist = formatList(attendance.ci_itemchkstatus);
  const endChecklist = formatList(attendance.co_itemchkstatus);

  const getImageUrl = (filename, type) => {
    if (!filename) return null;
    if (filename.startsWith("http")) return filename;
    let folder =
      type === "selfie"
        ? "/uploads/selfies"
        : type === "odometer"
          ? "/uploads/odometer"
          : type === "checklist"
            ? "/uploads/chklist_img"
            : "/uploads";
    return `${DRIVER_IMAGE_URL}${folder}/${filename}`;
  };

  const renderChecklistItem = (item, index) => {
    const isChecked = item.value == 1;
    const itemImage = item.image ? getImageUrl(item.image, "checklist") : null;
    return (
      <div
        key={index}
        className={`checklist-item ${isChecked ? "pass" : "fail"}`}
      >
        <div className="checklist-left">
          <span className="status-icon">
            {isChecked ? <CheckCircle size={16} /> : <XCircle size={16} />}
          </span>
          <span className="item-name">{item.name}</span>
        </div>
        {itemImage && (
          <div
            className="chk-thumb-wrapper"
            onClick={(e) => {
              e.stopPropagation();
              onImageClick(itemImage);
            }}
            title="View Full Image"
          >
            <img src={itemImage} alt={item.name} />
          </div>
        )}
      </div>
    );
  };

  const headerDateStr =
    reliableDateParse(attendance.checkin_datetime)?.toLocaleDateString(
      "en-IN",
    ) || "--";

    let totalKm = "--";
  if (
    attendance.odo_civalue && attendance.odo_covalue &&
    attendance.odo_civalue !== "NULL" && attendance.odo_covalue !== "NULL" &&
    !isNaN(attendance.odo_covalue) && !isNaN(attendance.odo_civalue)
  ) {
    totalKm = `${Number(attendance.odo_covalue) - Number(attendance.odo_civalue)} KM`;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-box premium-layout"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="modal-header-premium">
          <div>
            <h4
              className="mb-1 fw-bold text-dark"
              style={{ letterSpacing: "-0.5px" }}
            >
              {driver.driver_fstname} {driver.driver_mdlname}{" "}
              {driver.driver_lstname}
            </h4>
           <div className="d-flex align-items-center gap-2">
              <span className="d-flex align-items-center bg-dark text-white rounded-pill px-3 py-1 small fw-bold font-monospace border border-dark">
                {driver.driver_regno}
              </span>
              
              <span className="d-flex align-items-center bg-light text-muted rounded-pill px-2 py-1 small fw-bold border">
                <Calendar size={12} className="me-1" /> {headerDateStr}
              </span>
              
              <span className="d-flex align-items-center bg-success bg-opacity-10 text-success rounded-pill px-3 py-1 small fw-bold border border-success border-opacity-50">
                <Activity size={12} className="me-1" /> Total: {totalKm} running today
              </span>
            </div>
          </div>
          <button className="close-btn-premium" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* BODY */}
        <div className="modal-body-premium scrollable-content">
          <div className="row g-0 h-100">
            {/* === LEFT COLUMN: START SHIFT === */}
            <div className="col-12 col-md-6 border-end-md p-3 pb-5">
              <h6 className="section-title text-success mb-3 d-flex align-items-center">
                <Clock size={16} className="me-2" /> Shift Start
              </h6>

              <div className="detail-card mb-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <span
                      className="badge bg-success bg-opacity-10 text-success mb-1"
                      style={{ fontSize: "10px" }}
                    >
                      PUNCH IN
                    </span>
                    <h5 className="fw-bold mb-0 text-dark">
                      {formatTimeOnly(attendance.checkin_datetime)}
                    </h5>
                  </div>
                  <div className="text-end" style={{ maxWidth: "65%" }}>
                    <div className="loc-text text-muted small">
                      <MapPin size={12} className="me-1 text-success" />
                      {attendance.cigps_locname || "GPS Location Not Available"}
                    </div>
                  </div>
                </div>
                <div className="photo-container">
                  {attendance.checkin_selfie ? (
                    <div
                      className="standard-img-wrapper"
                      onClick={() =>
                        onImageClick(
                          getImageUrl(attendance.checkin_selfie, "selfie"),
                        )
                      }
                    >
                      <img
                        src={getImageUrl(attendance.checkin_selfie, "selfie")}
                        alt="Start Selfie"
                      />
                      <div className="hover-eye">
                        <Eye size={18} />
                      </div>
                    </div>
                  ) : (
                    <div className="no-photo">No Selfie</div>
                  )}
                </div>
              </div>

              <div className="detail-card bg-light-orange border-orange">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span
                    className="badge bg-warning text-dark bg-opacity-25"
                    style={{ fontSize: "10px" }}
                  >
                    DUTY START
                  </span>
                  <h6 className="fw-bold mb-0 text-dark">
                    {formatTimeOnly(attendance.duty_in_datetime)}
                  </h6>
                </div>
                <div className="loc-box mb-2">
                  <Navigation
                    size={14}
                    className="text-warning me-2 flex-shrink-0 mt-1"
                  />
                  <div>
                    <span
                      className="x-small text-muted text-uppercase d-block fw-bold"
                      style={{ fontSize: "9px" }}
                    >
                      Start Location
                    </span>
                    <span className="small fw-bold text-dark lh-sm d-block">
                      {attendance.ci_location || "N/A"}
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2 p-2 bg-white rounded border border-light shadow-sm">
                  <span className="small text-muted fw-bold">
                    <Activity size={14} className="me-1" /> Odometer
                  </span>
                  <span className="fw-bold font-monospace text-dark">
                    {attendance.odo_civalue || "0"} km
                  </span>
                </div>
                <div className="photo-container mb-3">
                  {attendance.ciodomtr_image ? (
                    <div
                      className="standard-img-wrapper"
                      onClick={() =>
                        onImageClick(
                          getImageUrl(attendance.ciodomtr_image, "odometer"),
                        )
                      }
                    >
                      <img
                        src={getImageUrl(attendance.ciodomtr_image, "odometer")}
                        alt="Start ODO"
                      />
                      <div className="hover-eye">
                        <Eye size={18} />
                      </div>
                    </div>
                  ) : (
                    <div className="no-photo">No Odo Img</div>
                  )}
                </div>
                <div className="checklist-box">
                  <div className="checklist-header text-warning">
                    <FileText size={12} className="me-1" /> Vehicle Inspection
                  </div>
                  <div className="checklist-items">
                    {startChecklist.length > 0 ? (
                      startChecklist.map((item, i) =>
                        renderChecklistItem(item, i),
                      )
                    ) : (
                      <span className="small text-muted fst-italic">
                        No Data
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* === RIGHT COLUMN: END SHIFT === */}
            <div className="col-12 col-md-6 p-3 pb-5">
              <h6 className="section-title text-danger mb-3 d-flex align-items-center">
                <Clock size={16} className="me-2" /> Shift End
              </h6>

              <div className="detail-card mb-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <span
                      className="badge bg-danger bg-opacity-10 text-danger mb-1"
                      style={{ fontSize: "10px" }}
                    >
                      PUNCH OUT
                    </span>
                    <h5 className="fw-bold mb-0 text-dark">
                      {formatTimeOnly(attendance.checkout_datetime)}
                    </h5>
                  </div>
                  <div className="text-end" style={{ maxWidth: "65%" }}>
                    <div className="loc-text text-muted small">
                      <MapPin size={12} className="me-1 text-danger" />
                      {attendance.cogps_locname || "GPS Location Not Available"}
                    </div>
                  </div>
                </div>
                <div className="photo-container">
                  {attendance.checkout_selfie ? (
                    <div
                      className="standard-img-wrapper"
                      onClick={() =>
                        onImageClick(
                          getImageUrl(attendance.checkout_selfie, "selfie"),
                        )
                      }
                    >
                      <img
                        src={getImageUrl(attendance.checkout_selfie, "selfie")}
                        alt="End Selfie"
                      />
                      <div className="hover-eye">
                        <Eye size={18} />
                      </div>
                    </div>
                  ) : (
                    <div className="no-photo">No Selfie</div>
                  )}
                </div>
              </div>

              <div className="detail-card bg-light-purple border-purple">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span
                    className="badge bg-primary bg-opacity-10 text-primary"
                    style={{ fontSize: "10px" }}
                  >
                    DUTY END
                  </span>
                  <h6 className="fw-bold mb-0 text-dark">
                    {formatTimeOnly(attendance.duty_out_datetime)}
                  </h6>
                </div>
                <div className="loc-box mb-2">
                  <Navigation
                    size={14}
                    className="text-primary me-2 flex-shrink-0 mt-1"
                  />
                  <div>
                    <span
                      className="x-small text-muted text-uppercase d-block fw-bold"
                      style={{ fontSize: "9px" }}
                    >
                      End Location
                    </span>
                    <span className="small fw-bold text-dark lh-sm d-block">
                      {attendance.co_location || "N/A"}
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2 p-2 bg-white rounded border border-light shadow-sm">
                  <span className="small text-muted fw-bold">
                    <Activity size={14} className="me-1" /> Odometer
                  </span>
                  <span className="fw-bold font-monospace text-dark">
                    {attendance.odo_covalue || "0"} km
                  </span>
                </div>
                <div className="photo-container mb-3">
                  {attendance.coodomtr_image ? (
                    <div
                      className="standard-img-wrapper"
                      onClick={() =>
                        onImageClick(
                          getImageUrl(attendance.coodomtr_image, "odometer"),
                        )
                      }
                    >
                      <img
                        src={getImageUrl(attendance.coodomtr_image, "odometer")}
                        alt="End ODO"
                      />
                      <div className="hover-eye">
                        <Eye size={18} />
                      </div>
                    </div>
                  ) : (
                    <div className="no-photo">No Odo Img</div>
                  )}
                </div>
                <div className="checklist-box">
                  <div className="checklist-header text-primary">
                    <FileText size={12} className="me-1" /> Vehicle Inspection
                  </div>
                  <div className="checklist-items">
                    {endChecklist.length > 0 ? (
                      endChecklist.map((item, i) =>
                        renderChecklistItem(item, i),
                      )
                    ) : (
                      <span className="small text-muted fst-italic">
                        No Data
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceTable;